/** Shared domain constants used by API, web, and server. */

/**
 * `displayName` the server sends for the shared share-link actor. The web app
 * recognises it and swaps in a translated label (see `localizeDisplayName`).
 */
export const ANONYMOUS_CLIENT_DISPLAY_NAME = 'Client';

/** ISO 3166-1 alpha-2 country codes with public-holiday sync support (Nager.Date API). */
export const SUPPORTED_HOLIDAY_COUNTRIES = ['US', 'BR', 'CA'] as const;
export type SupportedHolidayCountry = (typeof SUPPORTED_HOLIDAY_COUNTRIES)[number];

export enum Platform {
  INSTAGRAM = 'instagram',
  FACEBOOK = 'facebook',
  LINKEDIN = 'linkedin',
}

export enum AccountType {
  ORGANIC = 'organic',
  ADS = 'ads',
}

export enum IntegrationType {
  GMAIL = 'gmail',
  GOOGLE_DRIVE = 'google_drive',
  SLACK = 'slack',
  CANVA = 'canva',
  FATHOM = 'fathom',
}

export enum IntegrationStatus {
  CREATED = 'created',
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  ERROR = 'error',
}

export enum ClientKnowledgeStatus {
  PENDING = 'pending',
  LEARNING = 'learning',
  LEARNED = 'learned',
}

export enum EntityType {
  EMAIL = 'email',
  DOCUMENT = 'document',
}

/**
 * Events that can trigger a Slack notification for an agency. Extend this
 * enum (and `SLACK_NOTIFICATION_EVENT_TYPES` below) to wire up new events —
 * both the settings UI and the notifier read from these.
 */
export enum SlackNotificationEventType {
  SCHEDULED_POST_PUBLISH_FAILURE = 'scheduled_post_publish_failure',
}

/**
 * Where an event's notification is routed. `CLIENT` events post to that
 * client's Slack channel override when one is configured, falling back to
 * the agency's general channel; `AGENCY` events always use the general
 * channel.
 */
export enum SlackNotificationEventScope {
  AGENCY = 'agency',
  CLIENT = 'client',
}

export type SlackNotificationEventTypeMeta = {
  id: SlackNotificationEventType;
  name: string;
  description: string;
  scope: SlackNotificationEventScope;
};

export const SLACK_NOTIFICATION_EVENT_TYPES: Record<
  SlackNotificationEventType,
  SlackNotificationEventTypeMeta
> = {
  [SlackNotificationEventType.SCHEDULED_POST_PUBLISH_FAILURE]: {
    id: SlackNotificationEventType.SCHEDULED_POST_PUBLISH_FAILURE,
    name: 'Scheduled post publish failure',
    description:
      'A scheduled post failed to publish to a platform integration (Facebook, Instagram, etc.)',
    scope: SlackNotificationEventScope.CLIENT,
  },
};

export type SocialMediaPlatform = {
  id: Platform;
  name: string;
  icon: string;
  color: string;
  formats: PostFormat[];
  captionCharacterLimit: number;
};

export enum PostFormat {
  IMAGE = 'image',
  CAROUSEL = 'carousel',
  STORY = 'story',
  REEL = 'reel',
  ARTICLE = 'article',
  VIDEO = 'video',
}

export const SOCIAL_MEDIA_PLATFORMS: Record<Platform, SocialMediaPlatform> = {
  [Platform.INSTAGRAM]: {
    id: Platform.INSTAGRAM,
    name: 'Instagram',
    icon: 'brandInstagram',
    color: '#E1306C',
    formats: [PostFormat.IMAGE, PostFormat.CAROUSEL, PostFormat.STORY, PostFormat.REEL],
    captionCharacterLimit: 2200,
  },
  [Platform.FACEBOOK]: {
    id: Platform.FACEBOOK,
    name: 'Facebook',
    icon: 'brandFacebook',
    color: '#1877F2',
    formats: [PostFormat.IMAGE, PostFormat.CAROUSEL, PostFormat.STORY, PostFormat.REEL],
    captionCharacterLimit: 63206,
  },
  [Platform.LINKEDIN]: {
    id: Platform.LINKEDIN,
    name: 'LinkedIn',
    icon: 'brandLinkedin',
    color: '#0A66C2',
    formats: [PostFormat.IMAGE, PostFormat.ARTICLE, PostFormat.VIDEO],
    captionCharacterLimit: 2200,
  },
};

export enum VideoProvider {
  VEO3 = 'veo3',
  HEYGEN = 'heygen',
}

export enum VideoGenModel {
  VEO_3 = 'veo-3.1-generate-001',
  VEO_3_FAST = 'veo-3.1-fast-generate-001',
  HEYGEN_AVATAR = 'heygen-avatar',
}

export const VIDEO_GEN_MODEL_MAP: Record<
  VideoGenModel,
  { name: string; pricePerSecond: number; provider: VideoProvider }
> = {
  [VideoGenModel.VEO_3]: {
    name: 'Veo 3',
    pricePerSecond: 0.4,
    provider: VideoProvider.VEO3,
  },
  [VideoGenModel.VEO_3_FAST]: {
    name: 'Veo 3 Fast',
    pricePerSecond: 0.15,
    provider: VideoProvider.VEO3,
  },
  [VideoGenModel.HEYGEN_AVATAR]: {
    name: 'HeyGen Avatar',
    pricePerSecond: 0.033,
    provider: VideoProvider.HEYGEN,
  },
};

export enum WatchlistType {
  COMPETITOR = 'competitor',
}

export enum PostsProjectType {
  POST = 'post',
  ARTICLE = 'article',
  EMAIL_MARKETING = 'email_marketing',
}

export enum CadenceKind {
  WEEKDAY = 'weekday',
  MONTHLY = 'monthly',
}

export type PostsProjectTypeInfo = {
  id: PostsProjectType;
  name: string;
  /** Cadence kind new deliverable-type configs default to when first configured. */
  defaultCadenceKind: CadenceKind;
};

export const POSTS_PROJECT_TYPES: Record<PostsProjectType, PostsProjectTypeInfo> = {
  [PostsProjectType.POST]: {
    id: PostsProjectType.POST,
    name: 'Post',
    defaultCadenceKind: CadenceKind.WEEKDAY,
  },
  [PostsProjectType.ARTICLE]: {
    id: PostsProjectType.ARTICLE,
    name: 'Article',
    defaultCadenceKind: CadenceKind.MONTHLY,
  },
  [PostsProjectType.EMAIL_MARKETING]: {
    id: PostsProjectType.EMAIL_MARKETING,
    name: 'Email Marketing',
    defaultCadenceKind: CadenceKind.MONTHLY,
  },
};

/** LaunchDarkly flag keys (shared between client and server). */
export enum Flags {
  Ai = 'ai',
  Connect = 'connect',
  CreateAgency = 'createAgency',
  PublishInstagram = 'publishInstagram',
  Competitors = 'competitors',
  Preview = 'preview',
  CreatePostModal = 'createPostModal',
  UnderDevelopment = 'underDevelopment',
  HikerEnabled = 'hikerEnabled',
  EnableSignedUrls = 'enableSignedUrls',
  AiVideos = 'aiVideos',
}
